import { useEffect, useRef, useState } from 'react';
import duckBallUrl from '../../levels/duck/obstacles/ball.png';
import {
  DUCK_BALL_FLY_MS,
  DUCK_BALL_HOLD_MS,
  DUCK_BALL_SPAWN_STAGGER_MS,
  DUCK_BALL_SPAWN_WINDOW_MS,
} from './gameplayConstants';
import {
  DUCK_BALL_OVERLAY_TARGETS,
  duckBallOffscreenYPct,
} from './duckBallOverlayTargets';
import './LevelDuckBallOverlay.css';

type BallPhase = 'flyIn' | 'hold' | 'flyOut';

type ActiveBall = {
  id: number;
  targetIndex: number;
  phase: BallPhase;
  phaseElapsedMs: number;
};

type LevelDuckBallOverlayProps = {
  isPaused: boolean;
  onComplete: () => void;
};

const PHASE_MS: Record<BallPhase, number> = {
  flyIn: DUCK_BALL_FLY_MS,
  hold: DUCK_BALL_HOLD_MS,
  flyOut: DUCK_BALL_FLY_MS,
};

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

function easeInCubic(t: number): number {
  return t ** 3;
}

function ballYPct(ball: ActiveBall): number {
  const target = DUCK_BALL_OVERLAY_TARGETS[ball.targetIndex];
  const offY = duckBallOffscreenYPct(target.sizePct);
  const t = Math.min(1, Math.max(0, ball.phaseElapsedMs / PHASE_MS[ball.phase]));
  if (ball.phase === 'flyIn') {
    return offY + (target.yPct - offY) * easeOutCubic(t);
  }
  if (ball.phase === 'hold') {
    return target.yPct;
  }
  return target.yPct + (offY - target.yPct) * easeInCubic(t);
}

export function LevelDuckBallOverlay({
  isPaused,
  onComplete,
}: LevelDuckBallOverlayProps) {
  const [balls, setBalls] = useState<ActiveBall[]>([]);
  const ballsRef = useRef<ActiveBall[]>([]);
  const spawnRemainingMsRef = useRef(DUCK_BALL_SPAWN_WINDOW_MS);
  const spawnCooldownMsRef = useRef(0);
  const nextBallIdRef = useRef(1);
  const completedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (isPaused) {
      return;
    }
    let frameId = 0;
    let lastNow = performance.now();

    const tick = (now: number) => {
      const dt = now - lastNow;
      lastNow = now;

      let spawnRemaining = spawnRemainingMsRef.current - dt;
      if (spawnRemaining < 0) {
        spawnRemaining = 0;
      }
      spawnRemainingMsRef.current = spawnRemaining;

      let cooldown = spawnCooldownMsRef.current - dt;
      if (cooldown < 0) {
        cooldown = 0;
      }

      const nextBalls: ActiveBall[] = [];

      for (const ball of ballsRef.current) {
        let phase = ball.phase;
        let elapsed = ball.phaseElapsedMs + dt;
        let keep = true;
        while (elapsed >= PHASE_MS[phase]) {
          elapsed -= PHASE_MS[phase];
          if (phase === 'flyIn') {
            phase = 'hold';
          } else if (phase === 'hold') {
            phase = 'flyOut';
          } else {
            keep = false;
            break;
          }
        }
        if (keep) {
          nextBalls.push({
            id: ball.id,
            targetIndex: ball.targetIndex,
            phase,
            phaseElapsedMs: elapsed,
          });
        }
      }

      const occupied = new Set(nextBalls.map((b) => b.targetIndex));
      if (spawnRemaining > 0 && cooldown <= 0) {
        for (let i = 0; i < DUCK_BALL_OVERLAY_TARGETS.length; i += 1) {
          if (occupied.has(i)) {
            continue;
          }
          nextBalls.push({
            id: nextBallIdRef.current,
            targetIndex: i,
            phase: 'flyIn',
            phaseElapsedMs: 0,
          });
          nextBallIdRef.current += 1;
          cooldown = DUCK_BALL_SPAWN_STAGGER_MS;
          break;
        }
      }
      spawnCooldownMsRef.current = cooldown;

      ballsRef.current = nextBalls;
      setBalls(nextBalls);

      if (spawnRemaining <= 0 && nextBalls.length === 0 && !completedRef.current) {
        completedRef.current = true;
        onCompleteRef.current();
        return;
      }

      frameId = window.requestAnimationFrame(tick);
    };

    frameId = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isPaused]);

  return (
    <div className="level-duck-ball-overlay" aria-hidden>
      {balls.map((ball) => {
        const target = DUCK_BALL_OVERLAY_TARGETS[ball.targetIndex];
        return (
          <img
            key={ball.id}
            className="level-duck-ball-overlay__ball"
            src={duckBallUrl}
            alt=""
            style={{
              left: `${target.xPct}%`,
              top: `${ballYPct(ball)}%`,
              width: `${target.sizePct}%`,
            }}
          />
        );
      })}
    </div>
  );
}
